import { Button, Card, CardHeader, Field, Input, NativeSelect, Skeleton, useToast } from '@provenance/design';
import type { AmendmentLimits, AmendmentLimitsView } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';

const KEY = ['amendment-limits'];
const CHOICES = Array.from({ length: 11 }, (_, n) => n);

/**
 * How many times an exporter may amend one certificate before being told to apply for a new
 * one (D-029). A chamber-wide default, and an optional override per certificate type.
 *
 * Counts the exporter's amendments only — approved and still pending. The chamber's own
 * corrections and revoke-and-reissues never count.
 */
export function AmendmentLimitsCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<AmendmentLimitsView>('/api/console/configuration/amendment-limits'),
  });

  const [draft, setDraft] = useState<AmendmentLimits | null>(null);
  useEffect(() => {
    if (query.data) {
      setDraft({
        chamberDefault: query.data.chamberDefault,
        overrides: query.data.types.map((t) => ({ documentTypeId: t.documentTypeId, limit: t.limit })),
      });
    }
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: AmendmentLimits) =>
      api.patch<AmendmentLimitsView>('/api/console/configuration/amendment-limits', body),
    onSuccess: (data) => {
      queryClient.setQueryData(KEY, data);
      toast.success('Amendment limits saved', 'They apply to the next amendment request.');
    },
    onError: (error) =>
      toast.error('Could not save the limits', error instanceof ApiError ? error.message : ''),
  });

  if (query.isLoading || !query.data || !draft) return <Skeleton className="h-48" />;

  const dirty =
    draft.chamberDefault !== query.data.chamberDefault ||
    draft.overrides.some((o, i) => o.limit !== query.data!.types[i]?.limit);
  const describe = (n: number) => (n === 0 ? 'No amendments' : n === 1 ? '1 amendment' : `${n} amendments`);

  return (
    <Card>
      <CardHeader
        title="Amendments per certificate"
        description="How many times an exporter may amend one certificate. After that they are told to apply for a new one. Your own corrections and revoke-and-reissues never count."
        actions={
          canEdit && (
            <Button
              size="sm"
              disabled={!dirty || save.isPending || draft.chamberDefault < 0}
              onClick={() => save.mutate(draft)}
            >
              <Save aria-hidden className="size-4" />
              {save.isPending ? 'Saving…' : 'Save limits'}
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-4">
        <Field label="Chamber default" help="Applies to every certificate type without its own limit. 0 means amendments are not accepted.">
          <Input
            type="number"
            min={0}
            max={20}
            className="max-w-28"
            disabled={!canEdit}
            value={draft.chamberDefault}
            onChange={(e) => setDraft({ ...draft, chamberDefault: Math.max(0, Math.min(20, Number(e.target.value) || 0)) })}
          />
        </Field>

        {query.data.types.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {query.data.types.map((type, i) => (
              <Field key={type.documentTypeId} label={type.name}>
                <NativeSelect
                  disabled={!canEdit}
                  value={draft.overrides[i]?.limit ?? ''}
                  onChange={(e) => {
                    const overrides = [...draft.overrides];
                    overrides[i] = {
                      documentTypeId: type.documentTypeId,
                      limit: e.target.value === '' ? null : Number(e.target.value),
                    };
                    setDraft({ ...draft, overrides });
                  }}
                >
                  <option value="">Chamber default ({describe(draft.chamberDefault).toLowerCase()})</option>
                  {CHOICES.map((n) => (
                    <option key={n} value={n}>
                      {describe(n)}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
